import { z } from 'zod'
import './registry'
import { IsoDateSchema, MoneySchema, UlidSchema } from './common'
import { ContentLocaleSchema } from './content-pages'
import {
  OrderFulfilmentSchema,
  OrderMessageFromSchema,
  OrderStatusSchema,
  PaymentStatusSchema,
  ShippingAddressSchema,
  ShippingMethodSchema,
  orderLineKey,
  type Order,
  type OrderRefund,
} from './orders'
import { VatTreatmentSchema } from './transactions'
// Relative (not `@/`): consumed cross-package via the schemas barrel.
import { webshopVatBreakdown } from '../lib/webshop-vat'
// Pure integer arithmetic, no SDK — safe for the browser bundle this barrel feeds.
import { centsToMoney, moneyToCents } from '../lib/money'

/**
 * PUBLIC checkout + order-access wire shapes (api plan Phase 3B). Everything
 * here is anonymous-surface: the checkout is `anon-host`, the order reads are
 * magic-token-scoped (plan §7). Like schemas/shop.ts, these are PROJECTIONS —
 * `toShopOrder` is the one place a hub Order becomes customer-visible, so a
 * staff-only field (internalNote, unreadForSeller, buyer identifiers…) can
 * only leak by deliberate edit here.
 */

// ── Checkout ───────────────────────────────────────────────────────────────

/**
 * Address as the checkout form collects it. Field bounds are generous — the
 * NL-postcode strictness lives in the storefront's own validation
 * (lib/checkout-validation.ts); the API stays country-agnostic in shape, and
 * v1's D3 placeholder matrix restricts SHIPPING to NL in the handler, not in
 * the schema.
 */
export const CheckoutAddressSchema = z
  .object({
    street: z.string().trim().min(1).max(120),
    houseNumber: z.string().trim().min(1).max(12),
    addition: z.string().trim().max(12).nullable().default(null),
    postcode: z.string().trim().min(4).max(12),
    city: z.string().trim().min(1).max(80),
    /** ISO 3166-1 alpha-2, uppercase. */
    country: z.string().regex(/^[A-Z]{2}$/),
    /** Optional contact number for the carrier — length-bounded, not parsed. */
    phone: z.string().trim().max(30).nullable().default(null),
  })
  .strict()
  .openapi('CheckoutAddress')

/**
 * The most records one checkout may hold. Exported so the storefront's basket
 * enforces the SAME number: a basket allowed to grow past it can never check
 * out, and the customer would only see a validation error they cannot fix.
 */
export const CHECKOUT_MAX_ITEMS = 20

export const CheckoutRequestSchema = z
  .object({
    /** Idempotency key: one ULID per checkout attempt, reused across retries. */
    requestId: UlidSchema,
    /** 1-of-1 items — one entry per record, no quantities. */
    items: z
      .array(z.object({ recordId: UlidSchema }).strict())
      .min(1)
      .max(CHECKOUT_MAX_ITEMS),
    customer: z
      .object({
        email: z.string().trim().email().max(200),
        firstName: z.string().trim().min(1).max(80),
        lastName: z.string().trim().min(1).max(80),
      })
      .strict(),
    delivery: ShippingMethodSchema,
    /** Required for delivery `ship`; must be absent for `pickup` (superRefine). */
    address: CheckoutAddressSchema.optional(),
    locale: ContentLocaleSchema.default('nl'),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.delivery === 'ship' && v.address === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'address is required when delivery is ship',
        path: ['address'],
      })
    }
    if (v.delivery === 'pickup' && v.address !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'address must be omitted when delivery is pickup',
        path: ['address'],
      })
    }
    const ids = v.items.map(i => i.recordId)
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'duplicate recordId — every copy is unique',
        path: ['items'],
      })
    }
  })
  .openapi('CheckoutRequest')

export const CheckoutResponseSchema = z
  .object({
    orderId: UlidSchema,
    status: OrderStatusSchema,
    /**
     * The order's first magic-link token (plan §7, api plan §0.2), raw —
     * returned exactly once here and kept in the checkout session store. The
     * return/status page polls with it; the confirmation email carries its
     * own, fresh token.
     */
    statusToken: z.string(),
    /**
     * Where to send the customer to pay (the PSP's hosted checkout). Null only
     * when the PSP returned no checkout URL — the storefront then falls back to
     * the order page, which polls status.
     */
    payment: z.union([z.object({ redirectUrl: z.string() }), z.null()]),
  })
  .openapi('CheckoutResponse')

// ── Public order projections (magic-token-scoped reads) ────────────────────

export const ShopOrderItemSchema = z
  .object({
    /** Links back to the public catalogue record (same id as ShopRecord.id). */
    recordId: z.union([UlidSchema, z.null()]),
    description: z.string(),
    priceEur: MoneySchema,
    vatTreatment: z.union([VatTreatmentSchema, z.null()]),
    /**
     * Refunded on THIS line so far — `'0.00'` for a line nothing came back on.
     *
     * Per line, not just per order, because that is the question a buyer
     * actually has: a four-record order with one bad copy is refunded for that
     * copy, and "€ 19,99 terugbetaald" on the order tells them an amount
     * without telling them which record it was for.
     */
    refundedEur: MoneySchema.default('0.00'),
  })
  .openapi('ShopOrderItem')

/** The §10.2 breakdown, computed server-side so every surface agrees. */
export const ShopOrderVatSchema = z
  .object({
    vatAmount: MoneySchema,
    marginCount: z.number().int().nonnegative(),
    standardCount: z.number().int().nonnegative(),
    includesShipping: z.boolean(),
  })
  .openapi('ShopOrderVat')

export const ShopOrderSchema = z
  .object({
    id: UlidSchema,
    /** Human-facing number (`RV-2026-0834`); null on orders predating it. */
    orderNumber: z.union([z.string(), z.null()]),
    status: OrderStatusSchema,
    createdAt: IsoDateSchema,
    items: z.array(ShopOrderItemSchema),
    totalEur: MoneySchema,
    shippingEur: z.union([MoneySchema, z.null()]),
    shippingMethod: z.union([ShippingMethodSchema, z.null()]),
    shippingAddress: z.union([ShippingAddressSchema, z.null()]),
    fulfilment: z.union([OrderFulfilmentSchema, z.null()]),
    messageCount: z.number().int().nonnegative(),
    vat: ShopOrderVatSchema,
    /** Refunded across the whole order, lines and shipping together. */
    refundedTotalEur: MoneySchema.default('0.00'),
    /** Refunded shipping, which belongs to no line. */
    shippingRefundedEur: MoneySchema.default('0.00'),
    /**
     * Everything the customer paid is back with them.
     *
     * Decided by the API, not by comparing `refundedTotalEur` to `totalEur`
     * here: "fully refunded" is a policy the refund handler enforces its caps
     * with (lib/order-refund.ts), and a second definition in the storefront
     * could tell a customer their order was fully refunded on a rounding
     * difference the API disagrees with.
     */
    fullyRefunded: z.boolean().default(false),
  })
  .openapi('ShopOrder')

/** The slim poll target (api plan §0.2) — cheap enough for a 2–3s interval. */
export const ShopOrderStatusSchema = z
  .object({
    status: OrderStatusSchema,
    /**
     * The PSP's own view of the payment, or null for an order that never had
     * one. Deliberately distinct from `status`: the return page lands here
     * while the payment is still `pending`, and the order only becomes `paid`
     * once the webhook also moved the copies to sold.
     */
    paymentStatus: z.union([PaymentStatusSchema, z.null()]),
    /**
     * Human-facing number (`RV-2026-0834`) for the return page — allocation
     * is best-effort at checkout, so it can be null.
     */
    orderNumber: z.union([z.string(), z.null()]),
  })
  .openapi('ShopOrderStatusResponse')

/** POST /shop/orders/{orderId}/pay — where to send the customer to (re)try payment. */
export const ShopOrderPayResponseSchema = z
  .object({ checkoutUrl: z.string() })
  .openapi('ShopOrderPayResponse')

/** POST /shop/orders/{orderId}/cancel — the order's state after the call (idempotent). */
export const ShopOrderCancelResponseSchema = z
  .object({ status: z.literal('cancelled') })
  .openapi('ShopOrderCancelResponse')

export const ShopOrderMessageSchema = z
  .object({
    id: z.string(),
    from: OrderMessageFromSchema,
    body: z.string(),
    createdAt: IsoDateSchema,
  })
  .openapi('ShopOrderMessage')

export const PostShopOrderMessageRequestSchema = z
  .object({
    body: z.string().trim().min(1).max(2000),
    /** Idempotency key — doubles as the message id, so a retry can't double-post. */
    requestId: UlidSchema,
  })
  .strict()
  .openapi('PostShopOrderMessageRequest')

/**
 * Project a hub Order onto the customer-visible shape. The customer IS the
 * buyer, so their own address/fulfilment are included; staff fields
 * (internalNote, unreadForSeller, lastReadAt, channel bookkeeping) are not.
 */
/**
 * The refund facts the storefront view needs, supplied by the caller.
 *
 * Passed in rather than read here: the refund rows live behind a DynamoDB query
 * (lib/order-refund.ts), and this module is part of the schema barrel the
 * BROWSER imports — pulling that lib in would drag the AWS SDK into the
 * storefront bundle. The sums below are pure arithmetic over what is handed in;
 * `fullyRefunded` is policy, so it comes from the API side that owns it.
 */
export interface ShopOrderRefundView {
  refunds: readonly OrderRefund[]
  fullyRefunded: boolean
}

export function toShopOrder(
  order: Order,
  vatStandardRatePercent: number,
  refundView: ShopOrderRefundView = { refunds: [], fullyRefunded: false }
): ShopOrder {
  const { refunds, fullyRefunded } = refundView
  const refundedByLine = new Map<string, number>()
  let shippingRefundedCents = 0
  let refundedTotalCents = 0
  for (const refund of refunds) {
    for (const line of refund.lines) {
      refundedByLine.set(
        line.lineKey,
        (refundedByLine.get(line.lineKey) ?? 0) + moneyToCents(line.amountEur)
      )
    }
    if (refund.shippingEur) shippingRefundedCents += moneyToCents(refund.shippingEur)
    refundedTotalCents += moneyToCents(refund.totalEur)
  }

  // Money HAS come back on orders that carry no refund row at all: the webhook
  // that auto-refunds a race-lost sale wrote only `refundedTotal` (and mailed
  // the customer to say so) until it began writing a row too (2026-10-10), as
  // did the refund handler before refunds became child rows. Falling through to
  // "nothing refunded" would tell those customers, on their own order page,
  // that no money is coming back — while
  // contradicting a mail they already have. The order row is the fallback, and
  // its `refunded` status is then the only honest read of "in full".
  const hasRefundRows = refundedTotalCents > 0
  const legacyRefundedCents =
    !hasRefundRows && order.refundedTotal !== null ? moneyToCents(order.refundedTotal) : 0
  if (legacyRefundedCents > 0) refundedTotalCents = legacyRefundedCents
  return {
    id: order.id,
    orderNumber: order.orderNumber,
    status: order.status,
    createdAt: order.createdAt,
    items: order.items.map((item, index) => ({
      recordId: item.recordId,
      description: item.description,
      priceEur: item.priceEur,
      vatTreatment: item.vatTreatment,
      // The SAME key the refund was recorded against — `orderLineKey` is the one
      // definition of a line's identity, shared with the refund handler.
      refundedEur: centsToMoney(refundedByLine.get(orderLineKey(item, index)) ?? 0),
    })),
    totalEur: order.totalEur,
    shippingEur: order.shippingEur,
    shippingMethod: order.shippingMethod,
    shippingAddress: order.shippingAddress,
    fulfilment: order.fulfilment,
    messageCount: order.messageCount,
    vat: webshopVatBreakdown(
      order.items.map(item => ({ priceEur: item.priceEur, vatTreatment: item.vatTreatment })),
      order.shippingMethod === 'pickup' ? null : order.shippingEur,
      vatStandardRatePercent
    ),
    refundedTotalEur: centsToMoney(refundedTotalCents),
    shippingRefundedEur: centsToMoney(shippingRefundedCents),
    // A row-less refund has nothing to split per line, so "in full" can only be
    // read off the order's own status — which is what those paths set.
    fullyRefunded: legacyRefundedCents > 0 ? order.status === 'refunded' : fullyRefunded,
  }
}

export type CheckoutAddress = z.infer<typeof CheckoutAddressSchema>
/**
 * What the STOREFRONT sends (`z.input`, not `z.infer`): fields with a server
 * default — `addition`, `phone` — stay optional on the wire, so the client
 * can omit an empty one. That keeps a new optional field backward-compatible
 * with a not-yet-redeployed API, whose `.strict()` schema rejects unknown
 * keys it hasn't learned about.
 */
export type CheckoutRequest = z.input<typeof CheckoutRequestSchema>
export type CheckoutResponse = z.infer<typeof CheckoutResponseSchema>
export type ShopOrder = z.infer<typeof ShopOrderSchema>
export type ShopOrderItem = z.infer<typeof ShopOrderItemSchema>
export type ShopOrderMessage = z.infer<typeof ShopOrderMessageSchema>
export type ShopOrderPayResponse = z.infer<typeof ShopOrderPayResponseSchema>
export type ShopOrderCancelResponse = z.infer<typeof ShopOrderCancelResponseSchema>
export type ShopOrderStatusResponse = z.infer<typeof ShopOrderStatusSchema>
export type PostShopOrderMessageRequest = z.infer<typeof PostShopOrderMessageRequestSchema>
